import { defineStore } from 'pinia';

// inbox-104 — useModalStore: централизованный менеджер модалок.
// API:
//   const modals = useModalStore();
//   modals.open('create-link', { campaignId: 7 });   // открывает по ключу
//   modals.close();                                   // закрывает текущую
//   modals.setPending(true);                          // показать spinner в модалке
//   modals.pending                                    // boolean getter (для :disabled)
//
// ModalRoot.vue делает switch по modals.activeKey и рендерит соответствующий компонент.
// Использование в кнопках:
//   <button @click="modals.open('create-link')">+ Создать ссылку</button>

export type ModalKey =
  | 'create-link'
  | 'edit-link'
  | 'preview-link'
  | 'create-campaign'
  | 'create-bio'
  | 'add-domain'
  | 'add-api-key'
  | 'add-webhook'
  | 'invite-teammate'
  | 'invite-user'
  | 'change-role'
  | 'remove-user'
  | 'cancel-subscription'
  | 'confirm-delete'
  | 'add-payment-method'
  | 'import-csv'
  | 'upgrade-plan'
  | 'qr-customizer'
  | 'connect-integration'
  | 'ab-test'
  | 'routing-builder'
  | 'export'
  | 'bulk-ops'
  | 'onboarding-coach'
  | 'block-fraud'
  | 'add-widget'
  | null;

interface State {
  activeKey: ModalKey;
  payload: Record<string, unknown> | null;
  pendingFlag: boolean;
}

// Module-scope listeners: НЕ в Pinia state — vite-ssg сериализует state в JSON,
// Map превращается в {} при hydration → this.listeners.get падает.
// Callbacks — не данные UI, они ephemeral subscriptions, держим их вне реактивности.
const listeners = new Map<ModalKey, Array<(result?: unknown) => void>>();

export const useModalStore = defineStore('modal', {
  state: (): State => ({
    activeKey: null,
    payload: null,
    pendingFlag: false,
  }),
  getters: {
    isOpen: (s) => s.activeKey !== null,
    pending: (s) => s.pendingFlag,
  },
  actions: {
    open(key: ModalKey, payload: Record<string, unknown> | null = null): void {
      this.activeKey = key;
      this.payload = payload;
      this.pendingFlag = false;
      // Lock body scroll while modal открыт
      if (typeof document !== 'undefined') {
        document.body.style.overflow = 'hidden';
      }
    },
    close(): void {
      this.activeKey = null;
      this.payload = null;
      this.pendingFlag = false;
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
      }
    },
    setPending(flag: boolean): void {
      this.pendingFlag = flag;
    },
    /** Подписаться на событие «модалка успешно отработала» (для refresh-listeners). */
    on(key: ModalKey, cb: (result?: unknown) => void): () => void {
      let arr = listeners.get(key);
      if (!arr) {
        arr = [];
        listeners.set(key, arr);
      }
      arr.push(cb);
      return () => {
        const a = listeners.get(key);
        if (!a) return;
        const i = a.indexOf(cb);
        if (i >= 0) a.splice(i, 1);
      };
    },
    /** Дёргается из модалки на submit-success — все слушатели получают result. */
    emit(key: ModalKey, result?: unknown): void {
      const arr = listeners.get(key);
      if (!arr) return;
      for (const cb of arr) {
        try { cb(result); } catch { /* fail-quiet */ }
      }
    },
  },
});
